/* tslint:disable:max-line-length */

import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { APIConfigService } from '../apiconfig.service';

import * as __utils from '../yasag-utils';

import * as __model from '../model';

export interface ListParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** Punt recollida */
  codi_element?: string;
  /** Codi residu */
  codi_residu?: string;
  /** Codi fraccio */
  codi_fraccio?: string;
  /** Tipus servei */
  tipus_servei?: string;
  /** Ocultar */
  ocultar?: boolean;
  /** Filter only collection points that have polygon geometry */
  only_polygons?: boolean;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface CreateParams {
  data: __model.PuntRecollida;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface BulkDeleteParams {
  data: object;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportPuntsGeojsonParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportPuntsRecollidaParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportPuntsVerdsParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportPuntsGeojsonParams {
  data: __model.ImportGeoJSON;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportPuntsRecollidaParams {
  data: __model.ImportCSVWithDelete;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportPuntsVerdsParams {
  data: __model.ImportCSVWithDelete;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ReadParams {
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique integer value identifying this punt recollida. */
  id: number;
}

export interface UpdateParams {
  data: __model.PuntRecollida;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique integer value identifying this punt recollida. */
  id: number;
}

export interface PartialUpdateParams {
  data: __model.PuntRecollida;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique integer value identifying this punt recollida. */
  id: number;
}

export interface DeleteParams {
  /** A unique integer value identifying this punt recollida. */
  id: number;
}

@Injectable()
export class PuntRecollidaService {
  constructor(
    private http: HttpClient,
    private apiConfigService: APIConfigService) {}


  list(params: ListParams, multipart = false): Observable<__model.PuntRecollidaList> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      codi_element: params.codi_element,
      codi_residu: params.codi_residu,
      codi_fraccio: params.codi_fraccio,
      tipus_servei: params.tipus_servei,
      ocultar: params.ocultar,
      only_polygons: params.only_polygons,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<__model.PuntRecollidaList>(this.apiConfigService.options.apiUrl + `/punt_recollida/`, {params: queryParams});
  }

  /** Creates an attribute category */
  create(params: CreateParams, multipart = false): Observable<__model.PuntRecollida> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.PuntRecollida>(this.apiConfigService.options.apiUrl + `/punt_recollida/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Delete multiple collection points by their IDs. */
  bulkDelete(params: BulkDeleteParams, multipart = false): Observable<__model.PuntRecollidaBulkDelete> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.PuntRecollidaBulkDelete>(this.apiConfigService.options.apiUrl + `/punt_recollida/bulk_delete/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Export PuntRecollida to GeoJSON FeatureCollection format. Supports filtering by query parameters. */
  exportPuntsGeojson(params: ExportPuntsGeojsonParams, multipart = false): Observable<object> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<object>(this.apiConfigService.options.apiUrl + `/punt_recollida/export_punts_geojson/`, {params: queryParams});
  }

  /**
   *
   * Export collection points to CSV format (streaming for large datasets).
   *
   * This endpoint exports all collection points EXCEPT Punts Verds (PV).
   * The CSV includes coordinates, schedules, and fraction information.
   *
   * Exported columns:
   * - Codi element, x, y, Districte, Barri
   * - Observacions (ca, es, en)
   * - codi_fraccio
   *
   * The response is streamed for efficient handling of 20k+ records.
   *
   */
  exportPuntsRecollida(params: ExportPuntsRecollidaParams, multipart = false): Observable<File> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<File>(this.apiConfigService.options.apiUrl + `/punt_recollida/export_punts_recollida/`, {params: queryParams});
  }

  /**
   *
   * Export Punts Verds (green points) to CSV format (streaming for large datasets).
   *
   * This endpoint exports all collection points that belong to Punts Verds fraction (PV).
   *
   * Exported columns:
   * - Codi, Centre (name)
   * - Coordenada X, Coordenada Y
   * - Districte, Barri, Adreça
   * - Observacions (ca, es, en)
   * - Ocultar_busqueda
   * - Fracció, Tipus_servei
   *
   * The response is streamed for efficient handling of large datasets.
   *
   */
  exportPuntsVerds(params: ExportPuntsVerdsParams, multipart = false): Observable<File> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<File>(this.apiConfigService.options.apiUrl + `/punt_recollida/export_punts_verds/`, {params: queryParams});
  }

  /**
   *
   * Import punts de recollida from GeoJSON files.
   *
   * The GeoJSON must be a FeatureCollection with Polygon/MultiPolygon geometries.
   *
   * Required properties in each feature:
   * - Codi_Fracció: Fraction code (must exist in DB)
   * - Districte, Barri: District and neighborhood
   *
   * Optional properties:
   * - tipus_servei: Service type name (catalan)
   * - dl, dt, dc, dj, dv, ds, dg: Days of week (boolean)
   * - hora_inici, hora_fi: Collection hours
   * - Observacions_ca, Observacions_es, Observacions_en: Observations
   *
   * The import is asynchronous. Returns a task_id to track progress.
   *
   */
  importPuntsGeojson(params: ImportPuntsGeojsonParams, multipart = false): Observable<__model.TaskId> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.TaskId>(this.apiConfigService.options.apiUrl + `/punt_recollida/import_punts_geojson/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /**
   *
   * Import collection points from CSV file.
   *
   * The CSV must have the following columns:
   * - Codi element (required)
   * - x, y (coordinates in ED50 format)
   * - Districte, Barri
   * - dl, dt, dc, dj, dv, ds, dg (boolean days)
   * - hora_inici, hora_fi, hora_tanc_migdia, hora_obert_tarda
   * - Observacions_ca, Observacions_es, Observacions_en
   * - codi_fraccio (foreign key to Fraccio)
   *
   * Optional parameters:
   * - delete_existing_csv: If true, deletes all collection points with origen='csv' before importing.
   *   This allows a full replacement instead of update.
   *
   * The import is asynchronous. Returns a task_id to track progress.
   *
   */
  importPuntsRecollida(params: ImportPuntsRecollidaParams, multipart = false): Observable<__model.TaskId> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.TaskId>(this.apiConfigService.options.apiUrl + `/punt_recollida/import_punts_recollida/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /**
   *
   * Import Punts Verds (green points) from CSV file.
   *
   * The CSV must have the following columns:
   * - Codi (required - unique identifier)
   * - Centre (name of the green point)
   * - Coordenada X, Coordenada Y (coordinates)
   * - Districte, Barri, Adreça
   * - dl, dt, dc, dj, dv, ds, dg (boolean days)
   * - hora_inici, hora_fi, hora_tanc_migdia, hora_obert_tarda
   * - Observacions_ca, Observacions_es, Observacions_en
   * - Ocultar_busqueda (boolean)
   * - Fracció (foreign key to Fraccio - e.g., 'PV')
   * - Tipus_Punt_ verd (determines TipusServei: PVB/PVM → Punts Verds,
   *   PVZ → Punts Verds de Zona, PVZA → Punts Verds de Zona d'Amiant)
   *
   * Optional parameters:
   * - delete_existing_csv: If true, deletes all Punts Verds with origen='csv' before importing.
   *   This allows a full replacement instead of update.
   *
   * The import is asynchronous. Returns a task_id to track progress.
   *
   */
  importPuntsVerds(params: ImportPuntsVerdsParams, multipart = false): Observable<__model.TaskId> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.TaskId>(this.apiConfigService.options.apiUrl + `/punt_recollida/import_punts_verds/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Gets an attribute category with its attributes */
  read(params: ReadParams, multipart = false): Observable<__model.PuntRecollida> {
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      id: params.id,
    };
    return this.http.get<__model.PuntRecollida>(this.apiConfigService.options.apiUrl + `/punt_recollida/${pathParams.id}/`, {params: queryParams});
  }

  /** Updates an attribute category */
  update(params: UpdateParams, multipart = false): Observable<__model.PuntRecollida> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      id: params.id,
    };
    return this.http.put<__model.PuntRecollida>(this.apiConfigService.options.apiUrl + `/punt_recollida/${pathParams.id}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Updates an attribute category partially */
  partialUpdate(params: PartialUpdateParams, multipart = false): Observable<__model.PuntRecollida> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      id: params.id,
    };
    return this.http.patch<__model.PuntRecollida>(this.apiConfigService.options.apiUrl + `/punt_recollida/${pathParams.id}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Deletes an attribute category from the DB */
  delete(params: DeleteParams, multipart = false): Observable<string> {
    const pathParams = {
      id: params.id,
    };
    return this.http.delete(this.apiConfigService.options.apiUrl + `/punt_recollida/${pathParams.id}/`, {responseType: 'text'});
  }
}
